import { User } from '../interfaces/auth';
import { Project, ProjectMember } from '../interfaces/project';

/**
 * Verifica se o usuário atual é um observador (somente visualização)
 * 
 * @param user - O usuário atual logado
 * @returns true se o usuário for um observador
 */
export const isUserObserver = (user: User | null): boolean => {
  if (!user) return false;
  return !!user.isObserver;
};

/**
 * Verifica se o usuário atual é um observador específico no projeto
 * 
 * @param user - O usuário atual logado
 * @param project - O projeto a ser verificado
 * @returns true se o usuário for um observador neste projeto
 */
export const isProjectObserver = (user: User | null, project: Project | null): boolean => {
  if (!user || !project) return false;
  
  // Verifica se o usuário atual está na lista de observadores do projeto
  const isInObserversList = project.observers?.some(
    (observer: ProjectMember) => observer.id === user.id
  );
  
  return !!isInObserversList;
};

/**
 * Verifica se o usuário é um administrador do sistema
 * 
 * @param user - O usuário atual logado
 * @returns true se o usuário for um administrador
 */
export const isSystemAdmin = (user: User | null): boolean => {
  if (!user) return false;
  
  // Verifica se o usuário é administrador global
  const isGlobalAdmin = (
    user.role?.toLowerCase() === 'admin' || 
    user.role?.toLowerCase() === 'administrator' || 
    user.role?.toLowerCase() === 'developer' || 
    user.role?.toLowerCase() === 'dev' ||
    // Verificar se o título inclui "Administrador do Sistema"
    (typeof user.role === 'string' && user.role.toLowerCase().includes('administrador do sistema'))
  );
  
  return isGlobalAdmin;
};

/**
 * Verifica se o usuário tem permissão para editar o projeto
 * 
 * @param user - O usuário atual logado
 * @param project - O projeto a ser verificado
 * @returns true se o usuário tiver permissão para editar
 */
export const canEditProject = (user: User | null, project: Project | null): boolean => {
  if (!user || !project) return false;
  
  // Observadores não podem editar
  if (isProjectObserver(user, project)) return false;
  
  // Administradores do projeto podem editar
  const isAdmin = project.admins?.some(admin => admin.id === user.id) || 
                 project.administrators?.some(admin => admin.id === user.id);
  
  // Proprietário/criador do projeto pode editar
  const isOwner = project.ownerId === user.id || project.creatorId === user.id;
  
  // Usuário com função de administrador global pode editar
  const isGlobalAdmin = isSystemAdmin(user);
  
  return isAdmin || isOwner || isGlobalAdmin;
};
